import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { EmailFolder } from 'src/app/deebr/service/folder.service';

@Component({
    selector: 'app-folder-tree-select',
    templateUrl: './folder-tree-select.component.html',
    styleUrls: ['./folder-tree-select.component.scss'],
    standalone: false
})
export class FolderTreeSelectComponent implements OnInit, OnChanges {
  @Input() folders: EmailFolder[] = [];
  @Input() selectedFolderId: number | null = null;
  @Output() folderSelected = new EventEmitter<number | null>();
  @Input() disabledFolderIds: number[] = [];

  /** Term typed in the search bar */
  searchTerm = '';

  /** Holds expansion state for each folder id */
  expandedFolders: { [key: number]: boolean } = {};

  ngOnInit(): void {
    this.expandAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['folders']) {
      this.expandAll();
    }
  }

  /**
   * List of folders filtered by the current search term.  When no term is set
   * the entire tree is returned unchanged.
   */
  get filteredFolders(): EmailFolder[] {
    if (!this.searchTerm.trim()) {
      return this.folders;
    }

    const term = this.searchTerm.trim().toLowerCase();

    const filterRec = (items: EmailFolder[]): EmailFolder[] =>
      items
        .map((f) => {
          const children = f.children ? filterRec(f.children) : [];
          const matches = f.name.toLowerCase().includes(term);
          if (matches || children.length) {
            // Keep a shallow copy so we don’t mutate the original input
            return { ...f, children } as EmailFolder;
          }
          return null;
        })
        .filter((x): x is EmailFolder => x !== null);

    return filterRec(this.folders);
  }

  /** Called on each key‑up in the search field so we re‑expand the tree */
  onSearchChange(): void {
    // Always expand everything while a search term is present so matches are visible
    if (this.searchTerm.trim()) {
      this.expandAll();
    }
  }

  private expandAll(): void {
    this.expandedFolders = {};
    this.expandFolderRecursively(this.folders);
  }

  private expandFolderRecursively(folders: EmailFolder[]): void {
    folders?.forEach((folder) => {
      this.expandedFolders[folder.id] = true;
      if (folder.children?.length) {
        this.expandFolderRecursively(folder.children);
      }
    });
  }

  toggleExpand(folder: EmailFolder, event: Event): void {
    event.stopPropagation();
    this.expandedFolders[folder.id] = !this.expandedFolders[folder.id];
  }

  isExpanded(folder: EmailFolder): boolean {
    return !!this.expandedFolders[folder.id];
  }

  selectFolder(folderId: number | null): void {
    if (this.isDisabled(folderId === null ? null : { id: folderId } as any)) {
      return;
    }
    this.selectedFolderId = folderId;
    this.folderSelected.emit(folderId);
  }

  isDisabled(folder: EmailFolder | null): boolean {
    // root (“Accueil”) is represented by null
    if (folder === null) {
      return this.disabledFolderIds.includes(null as any);
    }
    return this.disabledFolderIds.includes(folder.id);
  }
}